"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFeatureFlagsLoaded = useFeatureFlagsLoaded;
exports.useFlagVariation = useFlagVariation;
exports.useTrackExperimentEvent = useTrackExperimentEvent;
const react_1 = require("react");
const store_1 = require("./store");
const subscribe = (onChange) => store_1.$ldClient.listen(onChange);
const getEntry = () => store_1.$ldClient.get();
// Pre-rendered HTML (Astro build, Gatsby SSR) is always produced without a client, so hydration
// must start from `undefined` too or React reports a mismatch; it re-renders with the live client
// straight after.
const getServerEntry = () => undefined;
const useLDEntry = () => (0, react_1.useSyncExternalStore)(subscribe, getEntry, getServerEntry);
/**
 * Whether the currently published LaunchDarkly client has settled. Until then `useFlagVariation`
 * may return the default, so flag-gated UI should decide per use case whether to render the default
 * or hold back. `false` before the first client settles, and briefly after consent is revoked while
 * the no-events replacement initialises.
 */
function useFeatureFlagsLoaded() {
    return useLDEntry()?.settled ?? false;
}
/**
 * Returns a getter for a flag's value. Calling the getter records an exposure event (when consent
 * allows events), so call it at the moment the visitor actually enters the experiment.
 */
function useFlagVariation(flagKey, defaultValue) {
    const entry = useLDEntry();
    // Keyed on the store entry, not the client: a client published before it settled is
    // re-published as a new entry once it has, so a default read in between isn't kept.
    const cacheRef = (0, react_1.useRef)(null);
    return (0, react_1.useCallback)(() => {
        if (!entry)
            return defaultValue;
        if (cacheRef.current?.value !== undefined &&
            cacheRef.current.entry === entry &&
            cacheRef.current.flagKey === flagKey) {
            return cacheRef.current.value;
        }
        const value = entry.client.variation(flagKey, defaultValue);
        cacheRef.current = { entry, value, flagKey };
        return value;
    }, [defaultValue, entry, flagKey]);
}
/**
 * Tracks a LaunchDarkly experiment metric event (distinct from Segment tracking). A no-op until
 * the client has settled, and sent only when advertising consent allows events.
 */
function useTrackExperimentEvent() {
    const entry = useLDEntry();
    return (0, react_1.useCallback)((eventKey, data, metricValue) => {
        entry?.client.track(eventKey, data, metricValue);
    }, [entry]);
}
